import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PlusCircle, GripVertical, X } from 'lucide-react';
import uuid from 'uuid/v4';

import UIImage from './ui/UIImage';
import useComponent from '../../../../store/useComponent';

const initialState = {
  type: 'Wishlist',
  headerImage: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  eventSlugname: '',
  steps: [
  ],
  background: {
    start: '#fff',
    end: '#fff',
  },
  endScreen: {
    text: {
      en: 'Thank you for recommending us your favourite artists.',
      es: 'Gracias por recomendarnos a tus artistas favoritos.',
      ca: 'Gràcies per recomanar-nos els teus artistes preferits.',
      pt: 'Obrigado por nos recomendar seus artistas favoritos.',
    },
    showTicketsButton: true, // oculta el boton de comprar entrdadas
  },
};

const Question = ({ data, handleInputLocalized, handleInput, handleDeleteStep, title, activeLanguage, index, provided }) => (
  <div
    ref={provided.innerRef}
    {...provided.draggableProps}
    className="relative flex items-center gap-2 mb-4 p-4  bg-indigo-lightest rounded-lg border-indigo border"
  >
    <div {...provided.dragHandleProps} className="cursor-move">
      <GripVertical className="text-gray-400" />
    </div>
    <div className="flex flex-col w-full">
      <span className="block uppercase racking-wide text-gray-700 text-xs font-bold mb-2">
        {title}
      </span>
      <label htmlFor="title" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
        Question Slug -
        <span className="uppercase font-normal"> (Must be unique, is used to filter responses on Mixpanel)</span>
        <input
          className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-300 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
          name="id"
          onChange={e => handleInput(index, e)}
          type="text"
          value={data.steps[index].id}
        />
      </label>
      <label htmlFor={`question-${index}`} className="block w-full uppercase tracking-wide text-gray-700 text-xs font-bold">
        Question
        <input
          id={`question-${index}`}
          className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-300 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
          name={`question-${index}`}
          onChange={e => handleInputLocalized(index, e)}
          type="text"
          value={data.steps[index].question[activeLanguage]}
        />
      </label>
    </div>
    <button
      type="button"
      onClick={() => handleDeleteStep(index)}
      className="absolute top-0 right-0 p-1 hover:bg-red-100 rounded-full transition-colors"
      aria-label="Delete step"
    >
      <X className="w-5 h-5 text-red-500" />
    </button>
  </div>
);

const Wishlist = ({ activeLanguage, components, handlerSetComponents, id }) => {
  const { data, actions } = useComponent(initialState);

  const {
    initializeComponent,
    setFormValue,
  } = actions;

  const componentIndex = components.findIndex(obj => obj.id === id);
  const { component } = components[componentIndex];

  useEffect(() => {
    if (component) initializeComponent(component);
  }, []);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormValue(name, value);
  };

  const handleInputQuestionId = (questionIndex, e) => {
    const { value } = e.target;
    const newSteps = [...data.steps];
    newSteps[questionIndex].id = value;
    setFormValue('steps', newSteps);
  };

  const handleInputLocalized = (questionIndex, e) => {
    const { value } = e.target;
    const newSteps = [...data.steps];
    newSteps[questionIndex].question[activeLanguage] = value;
    setFormValue('steps', newSteps);
  };

  const handleImage = (imageUrl) => {
    setFormValue('headerImage', imageUrl);
  };

  const addNewQuestion = () => {
    const newSteps = [...data.steps];
    newSteps.push({
      uuid: uuid(),
      id: '', // used on mixpanel to track the question
      question: {
        en: '',
        es: '',
        ca: '',
      },
    });
    setFormValue('steps', newSteps);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(data.steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFormValue('steps', items);
  };

  const handleBackgrounColor = (e) => {
    const { value, name } = e.target;

    const newBackground = {
      ...data.background,
      [name]: value,
    };

    setFormValue('background', newBackground);
  };

  const handleDeleteStep = (indexToDelete) => {
    const newSteps = data.steps.filter((_, index) => index !== indexToDelete);
    setFormValue('steps', newSteps);
  };

  useEffect(() => {
    const updatedComponent = {
      id,
      component: data,
    };

    handlerSetComponents(updatedComponent, componentIndex);
  }, [data]);

  const handleInputEndScreen = (e) => {
    const { value } = e.target;

    const newEndScreen = {
      ...data.endScreen,
      text: {
        ...data.endScreen.text,
        [activeLanguage]: value,
      },
    };
    setFormValue('endScreen', newEndScreen);
  };
  
  const handleShowTicketsButton = () => {
    const newEndScreen = {
      ...data.endScreen,
      showTicketsButton: !(data.endScreen?.showTicketsButton || false),
    };
    setFormValue('endScreen', newEndScreen);
  };
  
  return (
    <div className="text-black">
      <div className="flex-1">
        <label className="block uppercase tracking-wide text-black text-xs font-bold">
          Image
        </label>
        <UIImage
          imageUrl={data.headerImage}
          setImageUrl={handleImage}
          activeLanguage={activeLanguage}
        />
      </div>
      <div className="flex-1">
        <div className="w-full flex">
          <label htmlFor="title" className="flex-1 block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
            Wishlist ID
            <span className="uppercase font-normal"> (Must be unique, this is the event on Mixpanel where the responses are linked to)</span>
            <input
              className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-300 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
              name="eventSlugname"
              onChange={handleInput}
              type="text"
              value={data.eventSlugname}
            />
          </label>
          <div className="flex-1 uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 ml-2">
            Background Gradient
            <div className="flex">
              <label htmlFor="title" className="flex-1 block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mr-1">
                Start
                <div className="relative">
                  <input
                    className="appearance-none uppercase block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                    name="start"
                    type="text"
                    onChange={handleBackgrounColor}
                    value={data.background.start}
                  />
                  <input
                    className="absolute bg-transparent flex h-full items-center p-1 right-0 text-gray-700 top-0 cursor-pointer"
                    name="start"
                    type="color"
                    onChange={handleBackgrounColor}
                    value={data.background.start}
                  />
                </div>
              </label>
              <label htmlFor="title" className="flex-1 block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 ml-1">
                End
                <div className="relative">
                  <input
                    className="appearance-none uppercase block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                    name="end"
                    type="text"
                    onChange={handleBackgrounColor}
                    value={data.background.end}
                  />
                  <input
                    className="absolute bg-transparent flex h-full items-center p-1 right-0 text-gray-700 top-0 cursor-pointer"
                    name="end"
                    type="color"
                    onChange={handleBackgrounColor}
                    value={data.background.end}
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="questions">
            {provided1 => (
              <div
                {...provided1.droppableProps}
                ref={provided1.innerRef}
                className="space-y-4"
              >
                {data.steps.map((step, index) => (
                  <Draggable
                    key={step.uuid}
                    draggableId={step.uuid}
                    index={index}
                  >
                    {provided => (
                      <Question
                        data={data}
                        handleInputLocalized={handleInputLocalized}
                        handleInput={handleInputQuestionId}
                        handleDeleteStep={handleDeleteStep}
                        title={`Step ${index + 1}`}
                        activeLanguage={activeLanguage}
                        index={index}
                        provided={provided}
                      />
                    )}
                  </Draggable>
                ))}
                {provided1.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <button
          type="button"
          onClick={addNewQuestion}
          className="flex items-center gap-2 mt-4 px-4 py-2 bg-indigo text-white rounded hover:bg-blue-600 transition-colors border-indigo border"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Add Step
        </button>
          <label className="block uppercase tracking-wide text-black text-xs font-bold mt-4 mb-1">
            End Screen
          </label>
          <textarea
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-300 rounded py-3 px-4 mb-3 leading-tight focus:outline-none"
            type="text"
            name="endScreen"
            value={data.endScreen && data.endScreen.text ? data.endScreen.text[activeLanguage] : ''}
            onChange={handleInputEndScreen}
          />

        <div className="flex w-full my-6 items-center text-black">
          <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mr-2">Show Ticket Button</label>
          <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
            <input
              type="checkbox"
              value={data.endScreen?.showTicketsButton || false}
              onChange={handleShowTicketsButton}
              checked={data.endScreen?.showTicketsButton || false}
              id="ticketButtonCheckbox"
              className="outline-none toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
            />
            <label htmlFor="ticketButtonCheckbox" className="toggle-label block overflow-hidden h-6 rounded-full bg-grey cursor-pointer"></label>
          </div>
        </div>
      </div>
    </div>
  );
};

Wishlist.propTypes = {
  activeLanguage: PropTypes.oneOf(['en', 'es', 'ca', 'pt']).isRequired,
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  endScreen: PropTypes.shape({
    text: PropTypes.shape({
      en: PropTypes.string,
      es: PropTypes.string,
      ca: PropTypes.string,
      pt: PropTypes.string,
    }).isRequired,
    showTicketsButton: PropTypes.bool,
  }).isRequired,
};

Question.propTypes = {
  data: PropTypes.shape({
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      questionId: PropTypes.string.isRequired,
      question: PropTypes.shape({
        en: PropTypes.string,
        es: PropTypes.string,
        ca: PropTypes.string,
      }),
    })),
  }).isRequired,
  handleInputLocalized: PropTypes.func.isRequired,
  handleInput: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  activeLanguage: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  provided: PropTypes.shape({
    innerRef: PropTypes.func.isRequired,
    draggableProps: PropTypes.shape({}).isRequired,
    dragHandleProps: PropTypes.shape({}).isRequired,
  }).isRequired,
};

export default Wishlist;
